import { Directive, HostListener, Input } from '@angular/core';

import { GridIndexPath } from '../../components/grid-view/grid-view.component';

export interface ArrowNavigationDelegate {
  arrowNavigated(indexPath: GridIndexPath): void;
}

@Directive({
  selector: '[tsArrowNavigation]',
  standalone: false,
})
export class ArrowNavigationDirective {
  @Input() tsArrowNavigationEnabled = false;
  @Input() tsArrowNavigationAnchor: GridIndexPath | undefined;
  @Input() tsArrowNavigationRowsPerSection: number[] = [];
  @Input() tsArrowNavigationIncompleteRowIndexes: number[][] = [];
  @Input() tsArrowNavigationFullRowIndexes: number[] = [];
  @Input() tsArrowNavigationDelegate: ArrowNavigationDelegate | undefined;

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (!this.tsArrowNavigationEnabled || !this.tsArrowNavigationDelegate) return;

    const direction = this.arrowKeyToDirection(event.key);
    if (!direction) return;

    event.preventDefault();

    const current = this.tsArrowNavigationAnchor ?? { section: 0, row: 0, column: 0 };
    const next = this.getNextIndexPath(current, direction);
    if (!next) return;

    this.tsArrowNavigationDelegate.arrowNavigated(next);
  }

  private arrowKeyToDirection(key: string): 'up' | 'down' | 'left' | 'right' | undefined {
    switch (key) {
      case 'ArrowUp':
        return 'up';
      case 'ArrowDown':
        return 'down';
      case 'ArrowLeft':
        return 'left';
      case 'ArrowRight':
        return 'right';
      default:
        return undefined;
    }
  }

  private getNextIndexPath(
    current: GridIndexPath,
    direction: 'up' | 'down' | 'left' | 'right'
  ): GridIndexPath | undefined {
    if (!this.tsArrowNavigationRowsPerSection?.length) return undefined;

    const { section, row, column } = current;

    switch (direction) {
      case 'right': {
        const colCount = this.getColumnCount(section, row);
        if (column + 1 < colCount) return { section, row, column: column + 1 };
        return this.getFirstCellOfNextRow(section, row);
      }
      case 'left': {
        if (column - 1 >= 0) return { section, row, column: column - 1 };
        return this.getLastCellOfPreviousRow(section, row);
      }
      case 'down':
        return this.findNextRowWithColumn(section, row, column, 'forward');
      case 'up':
        return this.findNextRowWithColumn(section, row, column, 'backward');
    }
  }

  private findNextRowWithColumn(
    section: number,
    row: number,
    column: number,
    direction: 'forward' | 'backward'
  ): GridIndexPath | undefined {
    const step = direction === 'forward' ? 1 : -1;
    let s = section;
    let r = row + step;

    while (s >= 0 && s < this.tsArrowNavigationRowsPerSection.length) {
      const rowsInSection = this.tsArrowNavigationRowsPerSection[s];
      if (r < 0 || r >= rowsInSection) {
        s += step;
        if (s < 0 || s >= this.tsArrowNavigationRowsPerSection.length) return undefined;
        const next = this.tsArrowNavigationRowsPerSection[s];
        r = direction === 'forward' ? 0 : next - 1;
        continue;
      }
      if (column < this.getColumnCount(s, r)) return { section: s, row: r, column };
      r += step;
    }

    return undefined;
  }

  private getColumnCount(section: number, row: number): number {
    const isLastRow = row === this.tsArrowNavigationRowsPerSection[section] - 1;
    const columns = isLastRow ? this.tsArrowNavigationIncompleteRowIndexes[section] : this.tsArrowNavigationFullRowIndexes;
    return Array.isArray(columns) ? columns.length : 0;
  }

  private getFirstCellOfNextRow(section: number, row: number): GridIndexPath | undefined {
    if (row + 1 < this.tsArrowNavigationRowsPerSection[section]) return { section, row: row + 1, column: 0 };
    for (let s = section + 1; s < this.tsArrowNavigationRowsPerSection.length; s++) {
      if (this.tsArrowNavigationRowsPerSection[s] > 0) return { section: s, row: 0, column: 0 };
    }
    return undefined;
  }

  private getLastCellOfPreviousRow(section: number, row: number): GridIndexPath | undefined {
    if (row - 1 >= 0) {
      const colCount = this.getColumnCount(section, row - 1);
      return { section, row: row - 1, column: colCount - 1 };
    }
    for (let s = section - 1; s >= 0; s--) {
      if (this.tsArrowNavigationRowsPerSection[s] > 0) {
        const lastRow = this.tsArrowNavigationRowsPerSection[s] - 1;
        const colCount = this.getColumnCount(s, lastRow);
        return { section: s, row: lastRow, column: colCount - 1 };
      }
    }
    return undefined;
  }
}
